import DOMPurify from 'dompurify'
import { sounds } from './modules/sounds.js'
import { initScene } from './modules/scene.js'
import { initParticles } from './modules/particles.js'

// ── Utils ─────────────────────────────────────────────────────────────────────

function escapeHTML(text) {
    const map = { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#039;' }
    return String(text).replace(/[&<>"']/g, m => map[m])
}

// ── Supabase ──────────────────────────────────────────────────────────────────

const supabaseClient = (window.supabase?.createClient || window.Supabase?.createClient)(
    import.meta.env.VITE_SUPABASE_URL,
    import.meta.env.VITE_SUPABASE_ANON_KEY
)

// ── Scene + Particles (reuse shared modules) ──────────────────────────────────

const { scene, camera, renderer, loader } = initScene()
const { animateParticles, spawnDust } = initParticles(scene, loader)

// ── Mouse dust ────────────────────────────────────────────────────────────────

function handleMove(event) {
    const clientX = event.touches ? event.touches[0].clientX : event.clientX
    const clientY = event.touches ? event.touches[0].clientY : event.clientY
    const normX =  (clientX / window.innerWidth)  * 2 - 1
    const normY = -(clientY / window.innerHeight) * 2 + 1
    spawnDust(normX, normY, camera)
}

window.addEventListener('mousemove', handleMove)
window.addEventListener('touchmove', handleMove, { passive: true })

// ── Blog / Article data ───────────────────────────────────────────────────────

async function init() {
    const container     = document.getElementById('blog-container')
    const articleContent = document.getElementById('article-content')

    // ── Blog listing page ─────────────────────────────────────────────────────
    if (container) {
        const { data: posts, error } = await supabaseClient
            .from('posts')
            .select('*')
            .order('created_at', { ascending: false })

        if (error) {
            container.innerHTML = '<p style="color: var(--gold-dim)">The oracle is silent. Check RLS policies.</p>'
            return
        }

        if (!posts || posts.length === 0) {
            container.innerHTML = '<p style="color: var(--gold-dim)">The Rabbit Hole is empty for now...</p>'
            return
        }

        container.innerHTML = posts.map(post => `
            <a href="/article.html?slug=${encodeURIComponent(post.slug)}" class="post-card">
                <div class="post-date">${new Date(post.created_at).toLocaleDateString()}</div>
                <h3 class="post-title">${escapeHTML(post.title)}</h3>
                <p class="post-excerpt">${escapeHTML(post.excerpt || '')}</p>
            </a>
        `).join('')

        container.querySelectorAll('.post-card').forEach(card => {
            card.addEventListener('click', () => {
                sounds.click.currentTime = 0
                sounds.click.play().catch(() => {})
            })
        })
    }

    // ── Article page ──────────────────────────────────────────────────────────
    if (articleContent) {
        const slug = new URLSearchParams(window.location.search).get('slug')

        if (!slug) {
            articleContent.innerHTML = '<h2>No ritual found in the URL...</h2>'
            return
        }

        const { data: post, error } = await supabaseClient
            .from('posts')
            .select('*')
            .eq('slug', slug)
            .single()

        if (error || !post) {
            articleContent.innerHTML = '<h2 style="color: var(--gold)">Article lost in the void...</h2>'
            return
        }

        document.title = `${post.title} — Rabbit Hole`
        document.getElementById('post-title').innerText = post.title
        document.getElementById('post-date').innerText  = new Date(post.created_at).toLocaleDateString()

        const html = post.content
            .replace(/^# (.*$)/gim,   '<h1 style="color: var(--gold); margin: 40px 0;">$1</h1>')
            .replace(/^## (.*$)/gim,  '<h2 style="color: var(--gold); margin: 30px 0;">$1</h2>')
            .replace(/^### (.*$)/gim, '<h3 style="color: var(--gold); margin: 25px 0;">$1</h3>')
            .replace(/^\-\-\-$/gim,  '<div class="divider">✦ ── ✦ ── ✦</div>')
            .replace(/^\- (.*$)/gim,  '<li style="margin-left: 20px; color: rgba(255,255,255,0.7);">$1</li>')
            .replace(/\*\*(.*)\*\*/gim, '<b style="color: var(--gold)">$1</b>')
            .replace(/\n/gim, '<br>')

        articleContent.innerHTML = `<div style="text-align: left;">${
            DOMPurify.sanitize(html, {
                ALLOWED_TAGS: ['p', 'h1', 'h2', 'h3', 'strong', 'em', 'b', 'br', 'div', 'li'],
                ALLOWED_ATTR: ['style', 'class']
            })
        }</div>`
    }
}

// ── Render loop ───────────────────────────────────────────────────────────────

function animate() {
    requestAnimationFrame(animate)
    animateParticles(Date.now() * 0.001)
    renderer.render(scene, camera)
}

animate()
init()

// ── Click sound on all links ──────────────────────────────────────────────────

document.querySelectorAll('a').forEach(link => {
    link.addEventListener('click', () => {
        sounds.click.currentTime = 0
        sounds.click.play().catch(() => {})
    })
})
